import Ajv from 'ajv';

const ajv = new Ajv({ strict: false, allErrors: true, validateFormats: false });
export const stepName = node => String(node.data.label || node.data.name || node.data.type).replace(/[-_]/g, ' ');

// Derive the readable plan from the original edges; never flatten parallel branches.
export function planLayers(nodes, edges) {
  const pending = new Set(nodes.map(n => n.id));
  const layers = [];
  while (pending.size) {
    const ready = nodes.filter(n => pending.has(n.id) && !edges.some(e => e.target === n.id && pending.has(e.source)));
    if (!ready.length) throw new Error('This workflow contains a cycle. Open the graph to repair its connections.');
    layers.push(ready);
    ready.forEach(n => pending.delete(n.id));
  }
  if (edges.some(e => !nodes.some(n => n.id === e.source) || !nodes.some(n => n.id === e.target))) {
    throw new Error('A connection points to a missing step. Open the graph to repair it.');
  }
  return layers;
}

export function validateParameters(node, value) {
  const validate = ajv.compile(node.data.parameters || { type: 'object' });
  if (!validate(value)) throw new Error(validate.errors.map(e => `${e.instancePath || 'Configuration'} ${e.message}`).join('. '));
  return value;
}

// Deliberately bounded, schema-grounded editing. An LLM planner can produce this
// same proposal contract later without owning graph state or bypassing validation.
export function proposeConfiguration(text, nodes, selectedId) {
  const selected = nodes.find(n => n.id === selectedId);
  const named = nodes.filter(n => text.toLowerCase().includes(stepName(n).toLowerCase()));
  let target = selected || (named.length === 1 ? named[0] : null);
  let key, value;
  const set = text.match(/^(?:set|change)\s+([\w_]+)\s+to\s+([\s\S]+)$/i);
  const schedule = text.match(/^(?:run\s+)?(?:every weekday|weekdays|every day|daily)\s+(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?[.!]?$/i);
  if (schedule) {
    const timers = nodes.filter(n => n.data.type === 'workflow/timer');
    target = selected?.data.type === 'workflow/timer' ? selected : timers.length === 1 ? timers[0] : null;
    let hour = Number(schedule[1]);
    const minute = Number(schedule[2] || 0);
    if (minute > 59 || hour > (schedule[3] ? 12 : 23) || (schedule[3] && hour < 1)) throw new Error('Use a valid time, such as every weekday at 9 am.');
    if (schedule[3]) hour = hour % 12 + (schedule[3].toLowerCase() === 'pm' ? 12 : 0);
    key = 'cron';
    value = `${minute} ${hour} * * ${/weekday/i.test(schedule[0]) ? '1-5' : '*'}`;
  } else if (set) {
    key = set[1].toLowerCase();
    value = set[2].trim();
  } else if (selected?.data.parameters?.properties?.prompt) {
    key = 'prompt';
    value = text;
  } else {
    throw new Error('Select a step, then describe its instructions or say “set model to …”. For a schedule, try “every weekday at 9 am”. You can also use Configure for every available setting.');
  }
  if (!target) throw new Error('Select the step you want to change first.');
  const schema = target.data.parameters?.properties?.[key];
  if (!schema) throw new Error(`${stepName(target)} has no “${key}” setting. Open Configure to see its available settings.`);
  if (/secret|password|token|api.?key/i.test(key)) throw new Error('Use Configure to change credentials without putting them in the conversation.');
  if (schema.type && schema.type !== 'string') {
    try { value = JSON.parse(value); } catch { throw new Error(`“${key}” needs a ${schema.type} value.`); }
  }
  const before = target.data.parameter_data || {};
  const after = validateParameters(target, { ...before, [key]: value });
  if (JSON.stringify(before) === JSON.stringify(after)) throw new Error('That setting is already in place. No change needed.');
  return { nodeId: target.id, name: stepName(target), key, value, before: JSON.stringify(before), after };
}

// Check both the saved values and current schema before applying a reviewed edit.
export function validateProposal(proposal, current) {
  if (!current || JSON.stringify(current.data.parameter_data || {}) !== proposal.before) {
    throw new Error('This step changed. Create a new proposal.');
  }
  return validateParameters(current, proposal.after);
}
